import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { StatStrip } from './components/StatStrip';
import { api } from '../../utils/api';
import { Button } from '../../components/ui/button';
import { Card } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { toast } from 'sonner';
import { Loader2, Clock, Calendar, Users, AlertTriangle, CheckCircle2, Send, Flag } from 'lucide-react';

const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString('sv-SE', { day: 'numeric', month: 'short' }) : '—';
const fmtDateFull = (iso) => iso ? new Date(iso).toLocaleDateString('sv-SE') : '—';
const daysBetween = (a, b) => Math.max(0, Math.round((new Date(b) - new Date(a)) / (1000 * 60 * 60 * 24)));

const TimePlanTab = ({ projectId }) => {
  const [plans, setPlans] = useState([]);
  const [laborPlans, setLaborPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [form, setForm] = useState({
    labor_plan_id: '',
    start_date: new Date().toISOString().slice(0, 10),
    hours_per_day: 8,
    work_days_per_week: 5,
    respect_swedish_holidays: true,
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [t, l] = await Promise.all([
        api.listTimePlans(projectId),
        api.listLaborPlans(projectId),
      ]);
      setPlans(t.data?.plans || []);
      const lp = (l.data?.plans || []).filter(p => p.status === 'completed');
      setLaborPlans(lp);
      if (lp[0]?.id && !form.labor_plan_id) setForm(f => ({ ...f, labor_plan_id: lp[0].id }));
    } catch {
      toast.error('Kunde inte ladda tid-planer');
    } finally { setLoading(false); }
  }, [projectId]); // eslint-disable-line

  useEffect(() => { load(); }, [load]);

  // Poll generating plans
  useEffect(() => {
    const gens = plans.filter(p => p.status === 'generating');
    if (!gens.length) return;
    const iv = setInterval(async () => {
      const upd = await Promise.all(gens.map(p => api.getTimePlan(p.id).then(r => r.data).catch(() => null)));
      setPlans(prev => prev.map(p => upd.find(u => u?.id === p.id) || p));
    }, 4000);
    return () => clearInterval(iv);
  }, [plans]);

  const handleGenerate = async () => {
    if (!form.labor_plan_id) {
      toast.error('Välj en arbete-plan att bygga tidsplanen på');
      return;
    }
    setGenerating(true);
    try {
      await api.generateTimePlan({
        project_id: projectId,
        labor_plan_id: form.labor_plan_id,
        start_date: form.start_date || null,
        hours_per_day: parseFloat(form.hours_per_day) || 8,
        work_days_per_week: parseInt(form.work_days_per_week) || 5,
        respect_swedish_holidays: !!form.respect_swedish_holidays,
      });
      toast.success('Tid-beräkning startad — klar om ~1 min');
      setShowDialog(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Kunde inte starta beräkning');
    } finally { setGenerating(false); }
  };

  return (
    <div className="space-y-5" data-testid="time-plan-tab">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h2 className="text-xl font-bold flex items-center gap-2"><Clock size={19} className="text-[#F26B1F]" /> Tid &amp; Schema</h2>
          <p className="text-xs text-muted-foreground mt-0.5">AI räknar projektlängd, fas-schema och fördröjningsrisk från Arbete-planen. Publicera schemat till anställda när det är klart.</p>
        </div>
        <Button onClick={() => setShowDialog(true)} className="op-btn-primary !h-[42px]" data-testid="btn-generate-time-plan">
          <Clock size={14} className="mr-1.5" /> Beräkna tid
        </Button>
      </div>

      {loading && <Loader2 className="animate-spin mx-auto my-8 text-muted-foreground" />}

      {(() => {
        const p = plans.find((x) => x.status === 'completed');
        if (!p) return null;
        const d = (v) => (v ? new Date(v).toLocaleDateString('sv-SE') : '—');
        return <StatStrip testid="time-stat-strip" subtitle="Steg 3 av 4 · Tid" title={`${d(p.start_date)} → ${d(p.end_date)}`}
          stats={[{ label: 'Arbetsdagar', value: p.total_workdays ?? '—' }, { label: 'Kalenderdagar', value: p.total_calendar_days ?? '—' },
            { label: 'Faser', value: (p.phases || []).length }, { label: 'Risker att bevaka', value: (p.overall_risks || []).length, accent: true }]} />;
      })()}

      {!loading && plans.length === 0 && (
        <Card className="p-8 text-center text-muted-foreground">
          <Clock size={32} className="mx-auto mb-2 opacity-50" />
          <p className="text-sm">Ingen tid-plan än. Kör Material + Arbete först, sen klickar du "Beräkna tid".</p>
        </Card>
      )}

      {plans.map(p => <TimePlanCard key={p.id} plan={p} onReload={load} />)}

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-md" data-testid="time-dialog">
          <DialogHeader><DialogTitle>Beräkna tid &amp; schema</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label>Arbete-plan att bygga på *</Label>
              <select
                value={form.labor_plan_id}
                onChange={(e) => setForm(f => ({ ...f, labor_plan_id: e.target.value }))}
                className="w-full h-9 rounded border border-border bg-background px-2 text-sm"
                data-testid="time-labor-plan"
              >
                <option value="">— Välj —</option>
                {laborPlans.map(lp => (
                  <option key={lp.id} value={lp.id}>
                    {new Date(lp.created_at).toLocaleDateString('sv-SE')} · {(lp.phases || []).length} faser · {Math.round(lp.total_labor_hours || 0)} h
                  </option>
                ))}
              </select>
              {laborPlans.length === 0 && (
                <p className="text-xs text-red-600 mt-1">Du behöver först en godkänd Arbete-plan. Gå till Arbete &amp; Bemanning-fliken.</p>
              )}
            </div>
            <div><Label>Startdatum</Label><Input type="date" value={form.start_date} onChange={(e) => setForm(f => ({ ...f, start_date: e.target.value }))} data-testid="time-start-date" /></div>
            <div className="grid grid-cols-2 gap-2">
              <div><Label>Timmar per dag</Label><Input type="number" value={form.hours_per_day} onChange={(e) => setForm(f => ({ ...f, hours_per_day: e.target.value }))} /></div>
              <div><Label>Arbetsdagar/vecka</Label><Input type="number" min={1} max={7} value={form.work_days_per_week} onChange={(e) => setForm(f => ({ ...f, work_days_per_week: e.target.value }))} /></div>
            </div>
            <label className="flex items-center gap-2 text-xs cursor-pointer">
              <input type="checkbox" checked={form.respect_swedish_holidays} onChange={(e) => setForm(f => ({ ...f, respect_swedish_holidays: e.target.checked }))} />
              Hoppa över svenska röda dagar &amp; helgdagar
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>Avbryt</Button>
            <Button onClick={handleGenerate} disabled={generating || !form.labor_plan_id} className="bg-[hsl(16,100%,55%)] hover:bg-[hsl(16,100%,45%)] text-white" data-testid="submit-time-btn">
              {generating ? <><Loader2 size={14} className="mr-1 animate-spin" /> Startar...</> : <><Clock size={14} className="mr-1" /> Starta beräkning</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ---- TimePlanCard with Gantt ----
const TimePlanCard = ({ plan, onReload }) => {
  const [expanded, setExpanded] = useState(true);
  const [publishing, setPublishing] = useState(false);

  const isGenerating = plan.status === 'generating';
  const isFailed = plan.status === 'failed';

  const publish = async () => {
    setPublishing(true);
    try {
      await api.publishTimeSchedule(plan.id, !plan.schedule_published);
      toast.success(plan.schedule_published ? 'Schemat avpublicerat' : 'Schemat publicerat till anställda');
      onReload();
    } catch {
      toast.error('Kunde inte publicera');
    } finally { setPublishing(false); }
  };

  // Gantt range beräknas från faser
  const ganttRange = useMemo(() => {
    const phases = plan.phases || [];
    if (!phases.length) return null;
    const starts = phases.map(p => new Date(p.start_date).getTime());
    const ends = phases.map(p => new Date(p.end_date).getTime());
    const min = Math.min(...starts);
    const max = Math.max(...ends);
    const totalMs = Math.max(1, max - min);
    return { min, max, totalMs };
  }, [plan.phases]);

  const phaseColors = ['bg-blue-500', 'bg-emerald-500', 'bg-amber-500', 'bg-purple-500', 'bg-pink-500', 'bg-cyan-500', 'bg-orange-500', 'bg-indigo-500', 'bg-teal-500', 'bg-rose-500', 'bg-sky-500', 'bg-lime-500'];

  return (
    <Card className="overflow-hidden" data-testid={`time-card-${plan.id}`}>
      <button onClick={() => setExpanded(e => !e)} className="w-full p-4 text-left hover:bg-muted/30 flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2">
            <h4 className="font-semibold">Tid-plan · {fmtDateFull(plan.start_date)} → {fmtDateFull(plan.end_date)}</h4>
            {isGenerating && <span className="text-xs bg-amber-100 text-amber-800 px-2 py-0.5 rounded flex items-center gap-1"><Loader2 size={10} className="animate-spin" /> Beräknar</span>}
            {isFailed && <span className="text-xs bg-red-100 text-red-800 px-2 py-0.5 rounded">Fel</span>}
            {plan.status === 'completed' && plan.schedule_published && <span className="text-xs bg-emerald-100 text-emerald-800 px-2 py-0.5 rounded flex items-center gap-1"><CheckCircle2 size={10} /> Publicerat</span>}
            {plan.status === 'completed' && !plan.schedule_published && <span className="text-xs bg-blue-100 text-blue-800 px-2 py-0.5 rounded">Utkast</span>}
          </div>
          {!isGenerating && !isFailed && (
            <p className="text-xs text-muted-foreground mt-1">
              {plan.total_workdays} arbetsdagar · {plan.total_calendar_days} kalenderdagar · {Math.round(plan.total_hours || 0)} h · buffer {plan.recommended_buffer_days}d
            </p>
          )}
          {isFailed && <p className="text-xs text-red-600 mt-1">{plan.error_message}</p>}
        </div>
      </button>

      {expanded && !isGenerating && !isFailed && (
        <div className="border-t border-border bg-muted/10">
          {plan.ai_summary && (
            <div className="px-4 py-3 bg-background border-b border-border">
              <p className="text-xs font-semibold mb-1">AI Översikt</p>
              <p className="text-xs text-muted-foreground">{plan.ai_summary}</p>
            </div>
          )}

          {/* Gantt-vy */}
          {ganttRange && (
            <div className="p-4 bg-background" data-testid={`gantt-${plan.id}`}>
              <p className="text-xs font-semibold mb-2 flex items-center gap-1"><Calendar size={11} /> Gantt-tidslinje</p>
              <div className="space-y-1.5">
                {(plan.phases || []).map((ph, i) => {
                  const start = new Date(ph.start_date).getTime();
                  const end = new Date(ph.end_date).getTime();
                  const leftPct = ((start - ganttRange.min) / ganttRange.totalMs) * 100;
                  const widthPct = Math.max(1, ((end - start) / ganttRange.totalMs) * 100);
                  const color = phaseColors[i % phaseColors.length];
                  return (
                    <div key={i} className="flex items-center gap-2 group">
                      <div className="w-32 flex-shrink-0 text-xs truncate" title={ph.name}>
                        {/^\d+\./.test(ph.name || '') ? null : <span className="text-muted-foreground mr-1">{ph.order}.</span>}{ph.name}
                        {ph.critical_path && <Flag size={10} className="inline ml-1 text-red-500" />}
                      </div>
                      <div className="flex-1 relative h-6 bg-muted/40 rounded">
                        <div
                          className={`absolute h-full rounded ${color} opacity-85 group-hover:opacity-100 flex items-center justify-start pl-2 text-[10px] font-semibold text-white overflow-hidden transition-all`}
                          style={{ left: `${leftPct}%`, width: `${widthPct}%` }}
                        >
                          {ph.duration_workdays}d
                        </div>
                      </div>
                      <div className="w-32 flex-shrink-0 text-[10px] text-right text-muted-foreground">
                        {fmtDate(ph.start_date)} → {fmtDate(ph.end_date)}
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="flex justify-between mt-2 text-[10px] text-muted-foreground">
                <span>{fmtDateFull(plan.start_date)}</span>
                <span>↔ {plan.total_calendar_days} kalenderdagar</span>
                <span>{fmtDateFull(plan.end_date)}</span>
              </div>
            </div>
          )}

          {/* Faser detalj */}
          <div className="p-4 space-y-2 bg-muted/10">
            <p className="text-xs font-semibold mb-1">Fas-detaljer</p>
            {(plan.phases || []).map((ph, i) => (
              <div key={i} className="bg-background rounded p-3 border border-border" data-testid={`phase-${i}`}>
                <div className="flex items-center justify-between mb-1">
                  <div className="flex items-center gap-2">
                    <span className={`w-6 h-6 rounded flex items-center justify-center text-xs font-bold text-white ${phaseColors[i % phaseColors.length]}`}>{ph.order}</span>
                    <h5 className="font-semibold text-sm">{ph.name}</h5>
                    {ph.critical_path && <span className="text-[10px] text-red-600 font-bold flex items-center gap-0.5"><Flag size={10} /> KRITISK VÄG</span>}
                  </div>
                  <div className={`text-xs px-2 py-0.5 rounded ${ph.delay_risk_pct >= 30 ? 'bg-red-100 text-red-700' : ph.delay_risk_pct >= 20 ? 'bg-amber-100 text-amber-700' : 'bg-emerald-100 text-emerald-700'}`}>
                    Risk {ph.delay_risk_pct}%
                  </div>
                </div>
                <div className="grid grid-cols-4 gap-2 text-[11px] mb-1">
                  <div><span className="text-muted-foreground">Start</span><p className="font-medium">{fmtDateFull(ph.start_date)}</p></div>
                  <div><span className="text-muted-foreground">Slut</span><p className="font-medium">{fmtDateFull(ph.end_date)}</p></div>
                  <div><span className="text-muted-foreground">Längd</span><p className="font-medium">{ph.duration_workdays} ad</p></div>
                  <div><span className="text-muted-foreground">Buffert</span><p className="font-medium">+{ph.recommended_buffer_days}d</p></div>
                </div>
                {ph.delay_reasoning && <p className="text-[11px] text-muted-foreground italic">💡 {ph.delay_reasoning}</p>}
                {(ph.trades_summary || []).length > 0 && (
                  <p className="text-[11px] text-muted-foreground mt-1">
                    <Users size={10} className="inline mr-0.5" />
                    {ph.trades_summary.map(t => `${t.workers}× ${t.role}`).join(', ')}
                  </p>
                )}
              </div>
            ))}
          </div>

          {/* Övergripande risker */}
          {(plan.overall_risks || []).length > 0 && (
            <div className="px-4 py-3 bg-red-50 border-t border-red-200">
              <p className="text-xs font-semibold text-red-800 flex items-center gap-1 mb-1"><AlertTriangle size={11} /> Övergripande risker</p>
              <ul className="text-xs text-red-700 list-disc pl-4 space-y-0.5">
                {plan.overall_risks.map((r, i) => <li key={i}>{r}</li>)}
              </ul>
            </div>
          )}

          {/* Assignments */}
          {(plan.assignments || []).length > 0 && (
            <div className="px-4 py-3 bg-background border-t border-border">
              <p className="text-xs font-semibold mb-2 flex items-center gap-1"><Users size={11} /> Schema per anställd ({plan.assignments.length} tilldelningar)</p>
              <div className="space-y-1 max-h-48 overflow-y-auto">
                {(() => {
                  const byEmp = plan.assignments.reduce((acc, a) => { (acc[a.employee_id] = acc[a.employee_id] || { name: a.employee_name, role: a.role, items: [] }).items.push(a); return acc; }, {});
                  return Object.entries(byEmp).map(([eid, data]) => (
                    <div key={eid} className="text-xs flex items-start gap-2 py-1 border-b border-dashed border-border/40 last:border-0">
                      <span className="font-medium w-28 truncate">{data.name}</span>
                      <span className="text-muted-foreground w-20">{data.role}</span>
                      <span className="flex-1 text-muted-foreground">
                        {data.items.map((a, idx) => (
                          <span key={idx} className="inline-block mr-2">
                            {a.phase_name}: {fmtDate(a.start_date)}–{fmtDate(a.end_date)} ({Math.round(a.allocated_hours)}h)
                          </span>
                        ))}
                      </span>
                    </div>
                  ));
                })()}
              </div>
            </div>
          )}

          <div className="px-4 py-3 bg-background border-t border-border flex items-center justify-between">
            <div className="text-xs text-muted-foreground">
              {plan.schedule_published ? 'Schemat är synligt för dina anställda i deras app.' : 'Schemat är ett utkast — publicera för att dela med anställda.'}
            </div>
            <Button
              size="sm"
              onClick={publish}
              disabled={publishing}
              variant={plan.schedule_published ? 'outline' : 'default'}
              className={plan.schedule_published ? '' : 'bg-[hsl(16,100%,55%)] hover:bg-[hsl(16,100%,45%)] text-white'}
              data-testid={`publish-btn-${plan.id}`}
            >
              {publishing ? <Loader2 size={14} className="mr-1 animate-spin" /> : <Send size={14} className="mr-1" />}
              {plan.schedule_published ? 'Avpublicera schema' : 'Publicera schema'}
            </Button>
          </div>
        </div>
      )}
    </Card>
  );
};

export default TimePlanTab;
